import { Injectable, signal } from '@angular/core';

/**
 * POC 最小方案的附件类型。
 *
 * 图片不真正上传到 S3，仅用 `URL.createObjectURL` 生成本地预览地址，
 * 并在提交时把附件信息一并存入 mock 报告（localStorage），
 * 用于演示"司机拍照辅助诊断"的业务价值，避免后端依赖。
 * 真实开发阶段会替换为后端 preSignedUrl 上传后的真实地址。
 */
export interface VoiceAttachment {
  /** 本地预览 URL（objectURL），刷新后失效 */
  url: string;
  /** 原始文件名 */
  name: string;
  /** 是否为图片（可渲染缩略图） */
  isImage: boolean;
}

/**
 * Mock 报告库服务。
 *
 * POC 阶段用于存储 Driver 通过语音报报告创建的"事件报告"，避免占用后端开发资源。
 * 数据保存在 localStorage，供：
 * - Driver 确认后创建报告
 * - Test Engineer 在新版 deviation report list 查看/补充工程师字段
 *
 * 真实开发阶段将由后端 createDeviationReport 接口替代，本服务仅作演示。
 */
export interface MockDeviationReport {
  id: string;
  reportType: 'OTI' | 'LP';
  status: 'submitted' | 'in-review';
  /** 结构化字段：prefilled（车辆档案/mimic）+ 语音采集 + 派生 */
  fields: Record<string, unknown>;
  /** 工程师补充字段（engineer 区块） */
  engineerFields?: Record<string, unknown>;
  /** 司机上传的辅助诊断图片（POC 本地预览，见 VoiceAttachment） */
  attachments?: VoiceAttachment[];
  vehicleId?: string;
  driver?: string;
  createdAt: string;
  updatedAt?: string;
}

const STORAGE_KEY = 'tvms_mock_deviation_reports';

@Injectable({ providedIn: 'root' })
export class MockReportService {
  /** 报告列表信号 */
  reports = signal<MockDeviationReport[]>([]);

  constructor() {
    this.load();
  }

  /** 从 localStorage 加载报告 */
  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const data = raw ? (JSON.parse(raw) as MockDeviationReport[]) : [];
      this.reports.set(data);
    } catch {
      this.reports.set([]);
    }
  }

  /** 持久化到 localStorage */
  private persist(): void {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(this.reports()));
  }

  /** 获取全部报告（新→旧） */
  list(): MockDeviationReport[] {
    return [...this.reports()].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }

  /** 按 id 获取报告 */
  getById(id: string): MockDeviationReport | undefined {
    return this.reports().find(r => r.id === id);
  }

  /**
   * 创建报告（Driver 确认后调用）。
   * @returns 创建的报告
   */
  create(input: {
    reportType: 'OTI' | 'LP';
    fields: Record<string, unknown>;
    attachments?: VoiceAttachment[];
    vehicleId?: string;
    driver?: string;
  }): MockDeviationReport {
    const report: MockDeviationReport = {
      id: crypto.randomUUID(),
      reportType: input.reportType,
      status: 'submitted',
      fields: { ...input.fields },
      attachments: input.attachments?.length ? [...input.attachments] : undefined,
      vehicleId: input.vehicleId,
      driver: input.driver,
      createdAt: new Date().toISOString(),
    };
    this.reports.update(list => [report, ...list]);
    this.persist();
    return report;
  }

  /** 更新报告（工程师补充字段） */
  update(id: string, patch: Partial<MockDeviationReport>): void {
    this.reports.update(list =>
      list.map(r => (r.id === id ? { ...r, ...patch, updatedAt: new Date().toISOString() } : r))
    );
    this.persist();
  }

  /** 删除报告 */
  delete(id: string): void {
    this.reports.update(list => list.filter(r => r.id !== id));
    this.persist();
  }
}
